import { z } from 'zod';

// NOTE:
// This schema matches the JSON format you paste into the panel ("content_json").
// It is stored in Postgres across JSONB columns (metadata/legal/faq/action_plan/mind_map/glossary/simulation/social_snaps).

export const FAQItemSchema = z
  .object({
    question: z.string().min(1),
    answer: z.string().min(1),
  })
  .strict();

export const ActionPlanStepSchema = z
  .object({
    day: z.number().int().positive(),
    task_title: z.string().min(1),
    description: z.string().min(1),
  })
  .strict();

export const MindMapNodeSchema: z.ZodType<{
  label: string;
  children?: any[];
}> = z.lazy(() =>
  z
    .object({
      label: z.string().min(1),
      children: z.array(MindMapNodeSchema).optional(),
    })
    .strict(),
);

export const MindMapSchema = z
  .object({
    label: z.string().min(1).optional(),
    children: z.array(MindMapNodeSchema).optional(),
  })
  .passthrough();

export const GlossaryTermSchema = z
  .object({
    term: z.string().min(1),
    definition: z.string().min(1),
    context: z.string().optional(),
  })
  .strict();

export const SimulationSchema = z
  .object({
    scenario: z.string().min(1),
    challenge: z.string().min(1),
    solution: z.string().min(1),
  })
  .strict();

export const SocialSnapSchema = z
  .object({
    text: z.string().min(1),
    attribution: z.string().optional(),
    theme: z.string().optional(),
  })
  .passthrough();

export const SocialSnapsSchema = z.array(SocialSnapSchema);

export const KeyTakeawaySchema = z
  .object({
    title: z.string().min(1),
    insight: z.string().min(1),
  })
  .passthrough();

export const KeyTakeawaysSchema = z.array(KeyTakeawaySchema);

export const WhoIsThisForSchema = z.array(z.string().min(1));

export const UnplayRatingSchema = z
  .object({
    content_quality: z.number().optional(),
    actionability: z.number().optional(),
    timelessness: z.number().optional(),
    overall: z.number().optional(),
  })
  .passthrough();

export const UnplayAssessmentSchema = z
  .object({
    difficulty_level: z.string().optional(),
    estimated_impact: z.string().optional(),
    time_to_value: z.string().optional(),
    unplay_rating: UnplayRatingSchema.optional(),
    best_listening_context: z.string().optional(),
  })
  .passthrough();

export const BookMetadataSchema = z
  .object({
    title: z.string().min(1),
    subtitle: z.string().optional(),
    authors: z.array(z.string().min(1)).optional(),
    publication_date: z.string().optional(),
    'ISBN-13': z.union([z.string(), z.number()]).optional(),
    publishers: z.array(z.string().min(1)).optional(),
    original_page_count: z.number().int().optional(),
    category: z.array(z.string().min(1)).optional(),
    tags: z.array(z.string().min(1)).optional(),
    narrator_gender: z.string().optional(),
    short_tagline: z.string().optional(),
    full_description: z.string().optional(),
  })
  .passthrough();

export const LegalSchema = z
  .object({
    disclaimer_text: z.string().min(1).optional(),
    copyright_status: z.string().min(1).optional(),
  })
  .passthrough();

export const BookRawJsonSchema = z
  .object({
    metadata: BookMetadataSchema,
    legal: LegalSchema.optional(),
    faq: z.array(FAQItemSchema),
    action_plan: z.array(ActionPlanStepSchema),
    mind_map: MindMapSchema,
    glossary: z.array(GlossaryTermSchema),
    simulation: SimulationSchema,
    social_snaps: SocialSnapsSchema,
    key_takeaways: KeyTakeawaysSchema.optional(),
    who_is_this_for: WhoIsThisForSchema.optional(),
    unplay_assessment: UnplayAssessmentSchema.optional(),
  })
  .passthrough();

export type BookRawJson = z.infer<typeof BookRawJsonSchema>;

export function formatZodError(err: z.ZodError): string {
  return err.issues
    .map((i) => {
      const path = i.path.length ? i.path.join('.') : '(root)';
      return `${path}: ${i.message}`;
    })
    .join('\n');
}

